import { describeApiError, type FantasyContest, type FantasyTeam } from '@brush/api-client';
import { Spinner, useDebouncedValue } from '@brush/ui';
import { useMemo, useState } from 'react';
import {
  budgetUnit,
  checkRoster,
  formatAmount,
  formatLockTime,
  submissionDisclosures,
  type RosterProblem,
} from '../../fantasy/roster';
import { usePlayer } from '../../player';
import {
  useDeleteFantasyTeam,
  useFantasyPool,
  useUpsertFantasyTeam,
  useValidateFantasyRoster,
} from '../../queries';

/**
 * §9.2's lineup builder: the pool with salaries, a running budget meter, roster slots, and a
 * captain selector.
 *
 * <b>Two validators, and the split is deliberate.</b> `checkRoster` mirrors the server's rules so
 * the meter moves on a click and Submit is closed before anybody presses it; `POST /validate` is
 * the control, debounced, and <b>its messages are what an entrant reads wherever its answer is
 * current</b>. The mirror is feedback in the gap — the same relationship `points/validate.ts` has
 * to the server's definition validator, and the same reason: a lineup the screen calls legal and
 * the server refuses is a submission that bounces off a page showing no problems.
 *
 * <b>Most rules are unreachable by construction, and that is the point of the controls rather than
 * an excuse to skip the check.</b> You cannot pick the same player twice, pick past the roster
 * size, or pick somebody outside the pool, because the pool is the only list and a picked player
 * leaves it. What remains reachable is the budget, a missing captain, and a short roster — which
 * are exactly the three the meter and the slots are about.
 */
export function LineupBuilder({
  contest,
  entry,
}: {
  contest: FantasyContest;
  /** The entry this player already holds, if any. Editing replaces it (the POST is an upsert). */
  entry: FantasyTeam | null;
}) {
  const { theme, player, readOnly } = usePlayer();
  const poolQ = useFantasyPool(contest.id);
  const upsert = useUpsertFantasyTeam(contest.id);
  const withdraw = useDeleteFantasyTeam(contest.id);

  const unit = budgetUnit(contest);

  const [name, setName] = useState(() => entry?.name ?? '');
  const [picks, setPicks] = useState<string[]>(() => entry?.members.map((m) => m.playerId) ?? []);
  const [captain, setCaptain] = useState<string | null>(
    () => entry?.members.find((m) => m.role === 'captain')?.playerId ?? null,
  );
  const [search, setSearch] = useState('');
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);

  const pool = poolQ.data?.players ?? [];

  const prices = useMemo(
    () => new Map(pool.map((p) => [p.playerId, p.salary])),
    [pool],
  );
  const names = useMemo(
    () => new Map(pool.map((p) => [p.playerId, p.playerName])),
    [pool],
  );

  // The mirror: instant, and what closes Submit.
  const local = useMemo(
    () =>
      checkRoster({
        rosterSize: contest.rosterSize,
        budget: contest.budget,
        budgetUnitLabel: contest.budgetUnitLabel,
        captainEnabled: contest.captainEnabled,
        pool: prices,
        names,
        playerIds: picks,
        captainPlayerId: captain,
      }),
    [contest, prices, names, picks, captain],
  );

  // The control. Debounced because picking eight players is eight changes, and asked for only once
  // there is something to ask about — an empty roster is refused for a reason the screen already
  // knows and says better ("pick 8 players").
  //
  // Memoised, and that is load-bearing rather than tidy: `useDebouncedValue` restarts its timer
  // whenever the value's *identity* changes, so a fresh object literal per render would reset it
  // on every re-render this component has for any reason — a refetch elsewhere, a parent update —
  // and the answer would arrive late or, under anything re-rendering faster than the delay, never.
  const roster = useMemo(() => ({ picks, captain }), [picks, captain]);
  const debounced = useDebouncedValue(roster, 400);
  const settled =
    debounced.picks.length === picks.length &&
    debounced.picks.every((id, i) => picks[i] === id) &&
    debounced.captain === captain;

  const serverCheck = useValidateFantasyRoster({
    contestId: contest.id,
    playerIds: debounced.picks,
    captainPlayerId: debounced.captain,
    enabled: debounced.picks.length > 0,
  });

  // The server's own sentences wherever its answer describes the roster on screen; the mirror in
  // the gap. They agree — that is the phase gate — so this is about whose words are read, not
  // about which verdict wins.
  const serverIsCurrent = settled && !serverCheck.isFetching && serverCheck.data !== undefined;
  const problems: RosterProblem[] = serverIsCurrent
    ? (serverCheck.data?.problems ?? [])
    : local.problems;

  const spent = local.salarySpent;
  const remaining = contest.budget - spent;
  const nameMissing = name.trim().length === 0;

  if (poolQ.isLoading) return <Spinner label="Loading the player pool…" />;

  if (poolQ.isError) {
    return (
      <p role="alert" className="rounded-xl p-6 text-sm" style={{ background: theme.panel }}>
        <span style={{ color: theme.textMuted }}>Couldn’t load the player pool just now.</span>
      </p>
    );
  }

  // Staff reading somebody else's portal. A roster is the entrant's own act — the server refuses a
  // team submitted on another card to everybody, administrators included — so the builder shows
  // what is there and offers nothing.
  const canSubmit = !readOnly;

  const chosen = picks
    .map((id) => pool.find((p) => p.playerId === id))
    .filter((p): p is NonNullable<typeof p> => p !== undefined);

  const term = search.trim().toLowerCase();
  const available = pool
    .filter((p) => !picks.includes(p.playerId))
    .filter((p) => term.length === 0 || p.playerName.toLowerCase().includes(term));

  const card = { background: theme.panel, border: `1px solid ${theme.border}` };

  const toggle = (playerId: string) => {
    setPicks((current) => {
      if (current.includes(playerId)) {
        // Dropping the captain drops the armband with them, rather than leaving a captain named
        // for somebody who is no longer on the roster — which is a state the server refuses and
        // nobody meant.
        if (captain === playerId) setCaptain(null);
        return current.filter((id) => id !== playerId);
      }
      // Past the roster size the pick is refused rather than swapping somebody out: which player
      // to drop is the entrant's decision and guessing at it loses their work.
      if (current.length >= contest.rosterSize) return current;
      return [...current, playerId];
    });
  };

  return (
    <div className="space-y-5">
      {/* §9.2's disclosure, above the builder rather than beside the button. The zero-scoring rule
          is the central risk of poker fantasy and the most likely support question in the feature;
          somebody who meets it after the deadline has lost to a rule nobody told them. First in the
          list, and not collapsed. */}
      <section style={card} className="space-y-4 rounded-xl p-5">
        <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: theme.accent }}>
          Before you pick
        </h2>
        {submissionDisclosures(contest).map((item) => (
          <div key={item.key}>
            <h3 className="text-sm font-semibold">{item.heading}</h3>
            <p className="mt-0.5 text-xs leading-relaxed" style={{ color: theme.textMuted }}>
              {item.body}
            </p>
          </div>
        ))}
      </section>

      <BudgetMeter
        spent={spent}
        remaining={remaining}
        budget={contest.budget}
        unit={unit}
        picked={picks.length}
        rosterSize={contest.rosterSize}
      />

      <section style={card} className="rounded-xl p-5">
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider" style={{ color: theme.textMuted }}>
            Team name
          </span>
          <input
            type="text"
            value={name}
            maxLength={120}
            disabled={!canSubmit}
            onChange={(e) => setName(e.target.value)}
            placeholder={player ? `${player.name.split(' ')[0]}'s team` : 'My team'}
            className="mt-1 w-full rounded-lg px-3 py-2 text-sm outline-none"
            style={{ background: theme.panelLight, border: `1px solid ${theme.border}`, color: theme.text }}
          />
        </label>
      </section>

      <RosterSlots
        contest={contest}
        chosen={chosen}
        captain={captain}
        unit={unit}
        canEdit={canSubmit}
        onDrop={toggle}
        onCaptain={setCaptain}
      />

      {problems.length > 0 ? (
        <ul style={card} className="space-y-1.5 rounded-xl p-4" role="status">
          {problems.map((problem) => (
            <li key={`${problem.code}-${problem.message}`} className="text-xs leading-relaxed">
              {/* The server's sentence, rendered verbatim. It names the player and says what to do
                  about it; a client re-wording it from the code would be a second implementation
                  of rules nothing tests. */}
              <span style={{ color: theme.textMuted }}>{problem.message}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <PlayerPool
        players={available}
        unit={unit}
        search={search}
        onSearch={setSearch}
        canPick={canSubmit && picks.length < contest.rosterSize}
        full={picks.length >= contest.rosterSize}
        onPick={toggle}
      />

      {canSubmit ? (
        <section style={card} className="space-y-3 rounded-xl p-5">
          <button
            type="button"
            disabled={!local.isValid || nameMissing || upsert.isPending}
            onClick={() =>
              upsert.mutate({
                contestId: contest.id,
                name: name.trim(),
                entryIndex: null,
                playerIds: picks,
                captainPlayerId: captain,
              })
            }
            className="w-full rounded-lg py-3 text-sm font-bold transition disabled:opacity-40"
            style={{ background: theme.accent, color: theme.bg }}
          >
            {upsert.isPending
              ? 'Submitting…'
              : entry
                ? 'Save changes to my team'
                : 'Enter this contest'}
          </button>

          <p className="text-center text-[11px]" style={{ color: theme.textMuted }}>
            {nameMissing
              ? 'Give your team a name first.'
              : `You can change this as often as you like until ${formatLockTime(contest.locksAt)}.`}
          </p>

          {upsert.isError ? (
            <p role="alert" className="text-center text-xs" style={{ color: theme.accent }}>
              {describeApiError(upsert.error)}
            </p>
          ) : null}
          {upsert.isSuccess ? (
            <p className="text-center text-xs" style={{ color: theme.textMuted }}>
              Saved. Your team is entered.
            </p>
          ) : null}

          {entry ? (
            confirmWithdraw ? (
              <div className="flex flex-wrap items-center justify-center gap-2 text-xs">
                <span style={{ color: theme.textMuted }}>Withdraw your entry for good?</span>
                <button
                  type="button"
                  onClick={() => setConfirmWithdraw(false)}
                  className="rounded-lg px-3 py-1.5"
                  style={{ border: `1px solid ${theme.border}`, color: theme.text }}
                >
                  Keep it
                </button>
                <button
                  type="button"
                  disabled={withdraw.isPending}
                  onClick={() => withdraw.mutate(entry.id)}
                  className="rounded-lg px-3 py-1.5 disabled:opacity-40"
                  style={{ border: `1px solid ${theme.accent}`, color: theme.accent }}
                >
                  {withdraw.isPending ? 'Withdrawing…' : 'Withdraw'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmWithdraw(true)}
                className="mx-auto block text-[11px] underline underline-offset-2"
                style={{ color: theme.textMuted }}
              >
                Withdraw my entry
              </button>
            )
          ) : null}
        </section>
      ) : (
        <p style={card} className="rounded-xl p-4 text-center text-xs">
          <span style={{ color: theme.textMuted }}>
            You’re looking at somebody else’s portal, so there’s nothing to enter here.
          </span>
        </p>
      )}
    </div>
  );
}

/**
 * §9.2's running budget meter.
 *
 * <b>It leads with what is left, not what is spent.</b> The question while drafting is always
 * "can I afford this one" — a bar showing 172 of 200 used makes somebody do the subtraction on
 * every pick. Over budget it turns to the signal colour, which is the one place in this app orange
 * attaches to something other than "this is you": it is still not a control, and it is still about
 * the reader's own team.
 */
function BudgetMeter({
  spent,
  remaining,
  budget,
  unit,
  picked,
  rosterSize,
}: {
  spent: number;
  remaining: number;
  budget: number;
  unit: string;
  picked: number;
  rosterSize: number;
}) {
  const { theme } = usePlayer();
  const over = remaining < 0;
  const used = budget > 0 ? Math.min(spent / budget, 1) : 0;
  const slotsLeft = rosterSize - picked;

  return (
    <section
      style={{ background: theme.panel, border: `1px solid ${over ? theme.accent : theme.border}` }}
      className="rounded-xl p-5"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div>
          <div className="text-2xl font-bold" style={{ color: over ? theme.accent : theme.text }}>
            {formatAmount(Math.abs(remaining), unit)}
          </div>
          <div className="text-[10px] uppercase tracking-wider" style={{ color: theme.textMuted }}>
            {over ? 'Over budget' : 'Left to spend'}
          </div>
        </div>
        <div className="text-right text-xs" style={{ color: theme.textMuted }}>
          <div>
            {picked} of {rosterSize} picked
          </div>
          {/* The number that decides the next pick, and the reason the meter leads with what is
              left: eight slots and a budget is an average, and an entrant who has spent half of it
              on two players wants telling now rather than at the eighth. */}
          {slotsLeft > 0 && !over ? (
            <div>
              {formatAmount(remaining / slotsLeft, unit)} each for the last {slotsLeft}
            </div>
          ) : null}
        </div>
      </div>

      <div
        className="mt-3 h-2 overflow-hidden rounded-full"
        style={{ background: theme.panelLight }}
        role="img"
        aria-label={`${formatAmount(spent, unit)} of ${formatAmount(budget, unit)} spent`}
      >
        <div
          className="h-full transition-all"
          style={{ width: `${used * 100}%`, background: over ? theme.accent : theme.text }}
        />
      </div>
    </section>
  );
}

/** The roster as slots — filled ones with a price and the armband, empty ones as empty. */
function RosterSlots({
  contest,
  chosen,
  captain,
  unit,
  canEdit,
  onDrop,
  onCaptain,
}: {
  contest: FantasyContest;
  chosen: readonly { playerId: string; playerName: string; salary: number }[];
  captain: string | null;
  unit: string;
  canEdit: boolean;
  onDrop: (playerId: string) => void;
  onCaptain: (playerId: string | null) => void;
}) {
  const { theme } = usePlayer();
  const empty = Math.max(contest.rosterSize - chosen.length, 0);

  return (
    <section
      style={{ background: theme.panel, border: `1px solid ${theme.border}` }}
      className="overflow-hidden rounded-xl"
    >
      <header className="px-5 py-3" style={{ borderBottom: `1px solid ${theme.border}` }}>
        <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: theme.accent }}>
          Your team
        </h2>
        {contest.captainEnabled ? (
          <p className="mt-0.5 text-xs" style={{ color: theme.textMuted }}>
            Pick a captain — their score is multiplied by {contest.captainMultiplier ?? 1}. It is
            the one decision that separates you from somebody who picked the same players.
          </p>
        ) : null}
      </header>

      <ul>
        {chosen.map((p) => (
          <li
            key={p.playerId}
            className="flex items-center justify-between gap-3 px-5 py-2.5"
            style={{ borderBottom: `1px solid ${theme.border}` }}
          >
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate text-sm font-medium">{p.playerName}</span>
              {captain === p.playerId ? (
                <span
                  className="shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                  style={{ background: `${theme.accent}26`, color: theme.accent }}
                >
                  Captain
                </span>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <span className="text-xs" style={{ color: theme.textMuted }}>
                {formatAmount(p.salary, unit)}
              </span>
              {canEdit && contest.captainEnabled ? (
                <button
                  type="button"
                  onClick={() => onCaptain(captain === p.playerId ? null : p.playerId)}
                  className="rounded px-2 py-1 text-[10px] font-semibold uppercase tracking-wider transition"
                  style={{
                    border: `1px solid ${captain === p.playerId ? theme.accent : theme.border}`,
                    color: captain === p.playerId ? theme.accent : theme.textMuted,
                  }}
                >
                  {captain === p.playerId ? 'Captain' : 'Make captain'}
                </button>
              ) : null}
              {canEdit ? (
                <button
                  type="button"
                  onClick={() => onDrop(p.playerId)}
                  aria-label={`Drop ${p.playerName}`}
                  className="text-xs transition hover:opacity-70"
                  style={{ color: theme.textMuted }}
                >
                  ✕
                </button>
              ) : null}
            </div>
          </li>
        ))}

        {Array.from({ length: empty }, (_, i) => (
          <li
            key={`empty-${i}`}
            className="px-5 py-2.5 text-sm"
            style={{ borderBottom: `1px solid ${theme.border}`, color: theme.textMuted }}
          >
            Empty slot
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The pool, priced.
 *
 * <b>Dearest first</b>, because that is the order somebody drafts in: the expensive players are the
 * decisions and the floor is where the rest of the budget goes.
 *
 * <b>`overrideReason` is deliberately not rendered.</b> The pool carries it and the Member policy
 * returns it, but it is a note an operator wrote for operators — "not attending, priced down" is
 * useful; the other kind is not something to put in front of the player it is about. Same call that
 * keeps §6.2's event list on the admin-only route.
 */
function PlayerPool({
  players,
  unit,
  search,
  onSearch,
  canPick,
  full,
  onPick,
}: {
  players: readonly { playerId: string; playerName: string; salary: number; isUnproven: boolean }[];
  unit: string;
  search: string;
  onSearch: (next: string) => void;
  canPick: boolean;
  full: boolean;
  onPick: (playerId: string) => void;
}) {
  const { theme } = usePlayer();
  const sorted = useMemo(
    () => [...players].sort((a, b) => b.salary - a.salary || a.playerName.localeCompare(b.playerName)),
    [players],
  );

  return (
    <section
      style={{ background: theme.panel, border: `1px solid ${theme.border}` }}
      className="overflow-hidden rounded-xl"
    >
      <header className="space-y-2 px-5 py-3" style={{ borderBottom: `1px solid ${theme.border}` }}>
        <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: theme.accent }}>
          Players you can pick
        </h2>
        <input
          type="search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search by name"
          className="w-full rounded-lg px-3 py-2 text-sm outline-none"
          style={{ background: theme.panelLight, border: `1px solid ${theme.border}`, color: theme.text }}
        />
        {full ? (
          <p className="text-xs" style={{ color: theme.textMuted }}>
            Your team is full — drop somebody above to make room.
          </p>
        ) : null}
      </header>

      {sorted.length === 0 ? (
        <p className="p-6 text-center text-sm" style={{ color: theme.textMuted }}>
          {search.trim() ? 'Nobody in the pool matches that.' : 'Everybody in the pool is on your team.'}
        </p>
      ) : (
        <ul className="max-h-96 overflow-y-auto">
          {sorted.map((p) => (
            <li key={p.playerId} style={{ borderBottom: `1px solid ${theme.border}` }}>
              <button
                type="button"
                disabled={!canPick}
                onClick={() => onPick(p.playerId)}
                className="flex w-full items-center justify-between gap-3 px-5 py-2.5 text-left transition hover:bg-white/5 disabled:opacity-40"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{p.playerName}</div>
                  {/* §4.3's floor, in a player's words and never as a percentile (§9.3). It says
                      the room has not seen enough of them to price them apart — which is a real
                      thing to know while drafting, and true of most of any pool. */}
                  {p.isUnproven ? (
                    <div className="text-[10px]" style={{ color: theme.textMuted }}>
                      Not much form to go on
                    </div>
                  ) : null}
                </div>
                <span className="shrink-0 text-sm font-semibold" style={{ color: theme.textMuted }}>
                  {formatAmount(p.salary, unit)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
